import React, { Component } from "react";
import PropTypes from "prop-types";
import { FormControl } from "react-bootstrap";
import AppModal from "../../common/modal/AppModal";
import {
  LuPlay,
  LuLayers,
  LuCornerUpLeft,
  LuSkipForward,
  LuNavigation,
  LuExternalLink,
  LuMessageSquare,
} from "react-icons/lu";
import {
  ID_PREFIX_RICH_MARK,
  ID_PREFIX_SORTABLE_BOX,
  ID_PREFIX_CONTAINED_VIEW,
  PAGE_TYPES,
} from "../../../../common/constants";
import i18n from "i18next";
import { isSection, isContainedView, nextAvailName } from "../../../../common/utils";
import "./_oneClickEditModal.scss";
import IdealStudio from "../../../../core/editor/main";

// [choice, icon, label key, hint key]. A choice is the learner-facing action; it maps onto the
// stored connectMode at save time (sub -> new|existing, goto -> existing), so saved marks keep
// their old shape.
const CHOICES = [
  ["nothing", LuPlay, "marks.opt_nothing", "marks.opt_nothing_hint"],
  ["sub", LuLayers, "marks.opt_sub", "marks.opt_sub_hint"],
  ["back", LuCornerUpLeft, "marks.opt_back", "marks.opt_back_hint"],
  ["next", LuSkipForward, "marks.opt_next", "marks.opt_next_hint"],
  ["goto", LuNavigation, "marks.opt_goto", "marks.opt_goto_hint"],
  ["external", LuExternalLink, "marks.opt_external", "marks.opt_external_hint"],
  ["popup", LuMessageSquare, "marks.opt_popup", "marks.opt_popup_hint"],
];
const choiceOf = (mark) =>
  !mark || mark.connectMode === "new"
    ? "sub"
    : mark.connectMode === "existing"
      ? "goto"
      : mark.connectMode || "sub";
const pill = (on, disabled) => ({
  flex: 1,
  padding: "6px 10px",
  borderRadius: "8px",
  border: `1px solid ${on ? "#7c3aed" : "#e2e8f0"}`,
  background: on ? "#f5f3ff" : "#ffffff",
  color: "#111827",
  fontSize: "13px",
  opacity: disabled ? 0.5 : 1,
  cursor: disabled ? "not-allowed" : "pointer",
});

/**
 * Modal component to edit marks' configuration
 */
// http(s) only: no scheme -> prefix https://; any other scheme (javascript:, data:, ...) -> null.
// "host:port" is not a scheme.
export function normalizeWebUrl(raw) {
  const u = (raw || "").trim();
  if (!u) return null;
  if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(u)) return /^https?:\/\//i.test(u) ? u : null;
  return "https://" + u;
}

export default class OneClickEditModal extends Component {
  /**
   * Constructor
   * @param props
   */
  constructor(props) {
    super(props);
    this.state = {
      choice: "sub",
      subExisting: false,
      pauseUntilClicked: false, // spike
      displayMode: "navigate",
      newType: PAGE_TYPES.SLIDE,
      existingSelected: "",
      externalUrl: "",
      popupText: "",
      error: "",
    };
    this.toggleModal = this.toggleModal.bind(this);
  }

  /**
   * Before component receives props
   * Updates component's state with toolbar's state info
   * @param nextProps
   */
  componentWillReceiveProps(nextProps) {
    let current = nextProps.currentRichMark;
    if (!this.props.visible) {
      const mode = current && current.connectMode;
      this.setState({
        color: current ? current.color : null,
        choice: choiceOf(current),
        subExisting: false,
        pauseUntilClicked: !!(current && current.pauseUntilClicked), // spike
        displayMode: (current && current.displayMode) || "navigate",
        newType: nextProps.navItems[nextProps.navItemSelected]
          ? nextProps.navItems[nextProps.navItemSelected].type
          : "",
        existingSelected: mode === "existing" ? current.connection : "",
        externalUrl: mode === "external" ? current.connection : "",
        popupText: mode === "popup" ? current.connection : "",
        error: "",
      });
    }
  }

  /**
   * Render React Component
   * @returns {code}
   */
  render() {
    let marksType =
      this.props.pluginToolbar &&
      this.props.pluginToolbar.pluginId &&
      IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId) &&
      IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId).getConfig() &&
      IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId).getConfig().marksType
        ? IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId).getConfig().marksType
        : {};
    let current = this.props.currentRichMark;
    const { choice } = this.state;
    const allViews = this.returnAllViews(this.props);
    const cvViews = allViews.filter((v) => isContainedView(v.id));
    const destList = choice === "goto" ? allViews : cvViews;
    // A saved destination that no longer exists stays unresolved (placeholder + warning, save is
    // blocked) instead of silently retargeting to the first option.
    const destMissing =
      !!this.state.existingSelected && !allViews.some((v) => v.id === this.state.existingSelected);
    const destId = destMissing
      ? ""
      : destList.some((v) => v.id === this.state.existingSelected)
        ? this.state.existingSelected
        : destList[0] && destList[0].id;
    const disabledChoice = {
      back: this.props.containedViewSelected === 0,
      next:
        this.props.navItemsIds.indexOf(this.props.navItemSelected) >=
        this.props.navItemsIds.length - 1,
      goto: allViews.length === 0,
    };
    let plugin =
      this.props.pluginToolbar &&
      this.props.pluginToolbar.pluginId &&
      IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId)
        ? IdealStudio.Plugins.get(this.props.pluginToolbar.pluginId)
        : undefined;
    let newId = "";
    const handleCancel = () => {
      this.props.onOneClickEditModalToggled();
    };

    const footer = (
      <div className="app-modal__footer">
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          onClick={handleCancel}
        >
          {i18n.t("Cancel")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          onClick={() => {
            let title = "";
            newId = ID_PREFIX_CONTAINED_VIEW + Date.now();
            let newMark = current && current.id ? current.id : ID_PREFIX_RICH_MARK + Date.now();
            let connectMode =
              choice === "sub" ? (this.state.subExisting ? "existing" : "new") : choice;
            if (connectMode === "goto") connectMode = "existing";
            let color = this.state.color || marksType.defaultColor || "#222222";
            let connection = destId;
            const externalUrl = normalizeWebUrl(this.state.externalUrl);
            if (connectMode === "external" && this.state.externalUrl.trim() && !externalUrl) {
              this.setState({ error: i18n.t("marks.err_url_scheme") });
              return;
            }
            if (
              (connectMode === "existing" && !destId) ||
              (connectMode === "external" && !this.state.externalUrl.trim()) ||
              (connectMode === "popup" && !this.state.popupText.trim())
            ) {
              this.setState({
                error: i18n.t(
                  connectMode === "external"
                    ? "marks.err_url"
                    : connectMode === "popup"
                      ? "marks.err_text"
                      : "marks.err_dest"
                ),
              });
              return;
            }
            let name =
              connectMode === "existing"
                ? this.props.viewToolbars[connection].viewName
                : nextAvailName(i18n.t("oneclick_view"), this.props.viewToolbars, "viewName");
            title = title || nextAvailName(i18n.t("marks.new_mark"), this.props.marks, "title");
            let markState;
            let value = "0";
            if (plugin && plugin.validateValueInput) {
              let val = plugin.validateValueInput(value);
              if (val && val.isWrong) {
                this.setState({ error: val.message ? val.message : i18n.t("mark_input") });
                return;
              } else if (val && val.value) {
                value = val.value;
              }
            }
            let sortable_id = ID_PREFIX_SORTABLE_BOX + Date.now();
            switch (connectMode) {
              case "new":
                markState = {
                  mark: {
                    id: newMark,
                    origin: this.props.boxSelected,
                    title,
                    connection: newId,
                    color,
                    connectMode,
                    displayMode: this.state.displayMode,
                    value,
                  },
                  view: {
                    info: "new",
                    type: this.state.newType,
                    id: newId,
                    parent: { [newMark]: this.props.boxSelected },
                    boxes: this.state.newType === PAGE_TYPES.DOCUMENT ? [sortable_id] : [],
                    extraFiles: {},
                  },
                  viewToolbar: {
                    id: newId,
                    doc_type: this.state.newType,
                    viewName: name,
                    hideTitles: false,
                  },
                };
                break;
              case "existing":
                markState = {
                  mark: {
                    id: newMark,
                    origin: this.props.boxSelected,
                    title,
                    connection,
                    color,
                    connectMode,
                    displayMode: this.state.displayMode,
                    value,
                  },
                  view: {
                    info: "new",
                    type: this.state.newType,
                    id: newId,
                    parent: this.props.boxSelected,
                    name,
                    boxes: [],
                    extraFiles: {},
                  },
                };
                break;
              case "next":
                markState = {
                  mark: {
                    id: newMark,
                    origin: this.props.boxSelected,
                    title,
                    connection:
                      this.props.navItemsIds[
                        this.props.navItemsIds.indexOf(this.props.navItemSelected) + 1
                      ],
                    color,
                    connectMode: "next",
                    displayMode: this.state.displayMode,
                    value,
                  },
                  view: {
                    info: "new",
                    type: this.state.newType,
                    id: newId,
                    parent: this.props.boxSelected,
                    name,
                    boxes: [],
                    extraFiles: {},
                  },
                };
                break;
              default:
                // external / popup / nothing / back
                markState = {
                  mark: {
                    id: newMark,
                    origin: this.props.boxSelected,
                    title,
                    connection:
                      connectMode === "external"
                        ? externalUrl
                        : connectMode === "popup"
                          ? this.state.popupText
                          : "",
                    color,
                    connectMode,
                    displayMode: this.state.displayMode,
                    value,
                  },
                };
            }
            markState.mark.pauseUntilClicked = this.state.pauseUntilClicked; // spike: every connect mode
            if (this.props.marks[newMark] === undefined) {
              this.props.onRichMarkAdded(markState.mark, markState.view, markState.viewToolbar);
            } else {
              this.props.onRichMarkUpdated(markState.mark, markState.view, markState.viewToolbar);
            }
            this.props.onOneClickEditModalToggled();
          }}
        >
          {i18n.t("marks.save_changes")}
        </button>
      </div>
    );

    return (
      <AppModal
        className="richMarksModal"
        backdrop
        size="md"
        show={this.props.visible}
        onClose={handleCancel}
        title={i18n.t("marks.click_action")}
        footer={footer}
      >
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", marginBottom: "6px" }}>
          {i18n.t("marks.when_clicked")}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {CHOICES.map(([key, Icon, label, hint]) => {
            const disabled = !!disabledChoice[key];
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={choice === key}
                disabled={disabled}
                onClick={() => this.setState({ choice: key, error: "" })}
                style={{
                  ...pill(choice === key, disabled),
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  textAlign: "left",
                  flex: "none",
                }}
              >
                <Icon size={16} style={{ flex: "none", color: "#7c3aed" }} />
                <span style={{ fontWeight: 600 }}>{i18n.t(label)}</span>
                <span style={{ color: "#6b7280", fontSize: "12px" }}>{i18n.t(hint)}</span>
              </button>
            );
          })}
        </div>

        {["sub", "goto", "external", "popup"].includes(choice) && (
          <div style={{ marginTop: "12px" }}>
            {choice === "sub" && (
              <div style={{ display: "flex", gap: "6px", marginBottom: "8px" }}>
                {[
                  [false, "marks.sub_new"],
                  [true, "marks.sub_existing"],
                ].map(([existing, label]) => {
                  const disabled = existing && cvViews.length === 0;
                  return (
                    <button
                      key={label}
                      type="button"
                      disabled={disabled}
                      onClick={() => this.setState({ subExisting: existing, error: "" })}
                      style={pill(this.state.subExisting === existing, disabled)}
                    >
                      {i18n.t(label)}
                    </button>
                  );
                })}
              </div>
            )}
            {(choice === "goto" || (choice === "sub" && this.state.subExisting)) && (
              <FormControl
                as="select"
                value={destId || ""}
                onChange={(e) => this.setState({ existingSelected: e.target.value, error: "" })}
                style={{ width: "100%" }}
              >
                {destMissing && <option value="">{i18n.t("marks.dest_missing_option")}</option>}
                {destList.map((view) => (
                  <option key={view.id} value={view.id}>
                    {(this.props.viewToolbars[view.id] &&
                      this.props.viewToolbars[view.id].viewName) ||
                      view.label}
                  </option>
                ))}
              </FormControl>
            )}
            {destMissing &&
              (choice === "goto" || (choice === "sub" && this.state.subExisting)) && (
                <div style={{ marginTop: "6px", fontSize: "12px", color: "#b45309" }}>
                  {i18n.t("marks.dest_missing")}
                </div>
              )}
            {choice === "external" && (
              <FormControl
                type="text"
                value={this.state.externalUrl}
                onChange={(e) => this.setState({ externalUrl: e.target.value, error: "" })}
                placeholder="https://"
              />
            )}
            {choice === "popup" && (
              <FormControl
                as="textarea"
                rows={3}
                style={{ width: "100%" }}
                value={this.state.popupText}
                onChange={(e) => this.setState({ popupText: e.target.value, error: "" })}
                placeholder={i18n.t("marks.popup_placeholder")}
              />
            )}
          </div>
        )}

        {this.state.error && (
          <div style={{ marginTop: "8px", fontSize: "12px", color: "#dc2626" }}>
            {this.state.error}
          </div>
        )}

        <div
          className={`app-modal__toggle-card${this.state.pauseUntilClicked ? " app-modal__toggle-card--active" : ""}`}
          style={{ marginTop: "14px" }}
        >
          <div className="app-modal__toggle-info">
            <span className="app-modal__toggle-title">{i18n.t("marks.pause_until_clicked")}</span>
            <span className="app-modal__toggle-subtitle">{i18n.t("marks.pause_hint")}</span>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={!!this.state.pauseUntilClicked}
            className={`app-modal__toggle-switch${this.state.pauseUntilClicked ? " app-modal__toggle-switch--on" : ""}`}
            onClick={() => this.setState({ pauseUntilClicked: !this.state.pauseUntilClicked })} // spike
          >
            <span />
          </button>
        </div>
      </AppModal>
    );
  }

  /**
   * Mapping method that joins contained views and navItems in array but excluding the ones that can't be
   * @param props Component's props
   * @returns {Array} Array of views
   */
  returnAllViews(props) {
    let viewNames = [];
    props.navItemsIds.map((id) => {
      if (id === 0) {
        return;
      }
      if (props.navItems[id].hidden) {
        return;
      }
      if (!IdealStudio.Config.sections_have_content && isSection(id)) {
        return;
      }
      // We need to turn off this requisite in case there is no more pages available and we need to link to the same page the box is in
      if (props.containedViewSelected === 0 && props.navItemSelected === id) {
        return;
      }

      viewNames.push({ label: props.navItems[id].name, id: id });
    });
    Object.keys(props.containedViews).map((cv) => {
      if (cv === 0) {
        return;
      }

      if (props.containedViewSelected === cv) {
        return;
      }
      viewNames.push({ label: props.containedViews[cv].name, id: props.containedViews[cv].id });
    });
    return viewNames;
  }

  /**
   * Method used to remap navItems and containedViews together
   * @param objects
   * @returns {*}
   */
  remapInObject(...objects) {
    return Object.assign({}, ...objects);
  }
  toggleModal(e) {
    let key = e.keyCode ? e.keyCode : e.which;
    if (key === 27 && this.props.visible) {
      this.props.onOneClickEditModalToggled();
    }
  }
  componentDidMount() {
    window.addEventListener("keyup", this.toggleModal);
  }
  componentWillUnmount() {
    window.removeEventListener("keyup", this.toggleModal);
  }
}

OneClickEditModal.propTypes = {
  /**
   * Caja seleccionada
   */
  boxSelected: PropTypes.any.isRequired,
  /**
   * Toolbar del plugin
   */
  pluginToolbar: PropTypes.object,
  /**
   * Current selected view (by ID)
   */
  navItemSelected: PropTypes.any.isRequired,
  /**
   * Selected contained view
   */
  containedViewSelected: PropTypes.any.isRequired,
  /**
   * Object containing all contained views (identified by its ID)
   */
  containedViews: PropTypes.object.isRequired,
  /**
   * Object containing all views (by id)
   */
  navItems: PropTypes.object.isRequired,
  /**
   * Indica si se muestra o oculta el modal de edición de marcas
   */
  visible: PropTypes.any.isRequired,
  /**
   * Mark currently being edited
   */
  currentRichMark: PropTypes.any,
  /**
   * Creates a new mark
   */
  onRichMarkAdded: PropTypes.func.isRequired,
  /**
   * Updates a mark
   */
  onRichMarkUpdated: PropTypes.func.isRequired,
  /**
   * Show/hide marks modal form
   */
  onOneClickEditModalToggled: PropTypes.any.isRequired,
  /**
   * Cursor value when creating mark (coordinates)
   */
  markCursorValue: PropTypes.any,
  /**
   * Object containing all the marks
   */
  marks: PropTypes.object,
  /**
   * Object containing all the viewTollbars
   */
  viewToolbars: PropTypes.object,
  /**
   *  Object containing all created boxes (by id)
   */
  boxes: PropTypes.object,
  /**
   * Function for adding a new box
   */
  onBoxAdded: PropTypes.func,
  /**
   * Function for getting the id of the selected template
   */
  onIndexSelected: PropTypes.func,
  /**
   * Contains the id of the selected template
   */
  indexSelected: PropTypes.func,
};
